import { useCallback, useState, useEffect } from 'react'

const extensionShadowDomSelectors = {
  extension: 'grammarly-desktop-integration',
  goSidePanel: 'grammarly-assistant-notch-view',
} as const

type ExtensionShadowDomSelectors = typeof extensionShadowDomSelectors
type ExtensionShadowDomSelector = ExtensionShadowDomSelectors[keyof ExtensionShadowDomSelectors]

const elementExists = (element: string): boolean =>
  IS_CLIENT_SIDE && window?.document?.getElementsByTagName(element)?.length > 0

const RETRY_INTERVAL_MS = 300
const RETRY_TIMES = 5

interface UseExtensionAvailability {
  isExtensionInstalled: boolean
  isGoAvailable: boolean
  isExtensionWithoutGo: boolean
  getIsGoAvailable: () => boolean
}

export const useExtensionAvailability = (): UseExtensionAvailability => {
  const [isExtensionInstalled, setIsExtensionInstalled] = useState(false)
  const [isGoAvailable, setIsGoAvailable] = useState(false)

  const findShadowDomElement = useCallback(
    (element: ExtensionShadowDomSelector, retryTimes = RETRY_TIMES) => {
      const shadowDomElementExists = elementExists(element)

      if (!shadowDomElementExists) {
        if (retryTimes > 0) {
          return setTimeout(() => {
            findShadowDomElement(element, retryTimes - 1)
          }, RETRY_INTERVAL_MS)
        }
        return
      }

      if (element === extensionShadowDomSelectors.extension) {
        setIsExtensionInstalled(shadowDomElementExists)
      }
      if (element === extensionShadowDomSelectors.goSidePanel) {
        setIsGoAvailable(shadowDomElementExists)
      }
    },
    [],
  )

  const getIsGoAvailable = useCallback(() => {
    return elementExists(extensionShadowDomSelectors.goSidePanel)
  }, [])

  useEffect(() => {
    const timeouts = Object.values(extensionShadowDomSelectors).map(selector =>
      findShadowDomElement(selector),
    )

    return () => {
      for (const timeout of timeouts) {
        clearTimeout(timeout)
      }
    }
  }, [findShadowDomElement])

  const isExtensionWithoutGo = isExtensionInstalled && !isGoAvailable

  return {
    isExtensionInstalled,
    isGoAvailable,
    isExtensionWithoutGo,
    getIsGoAvailable,
  }
}
